import React, { useContext } from 'react';
import styled from 'styled-components';

import Icon from '../../misc/Icon';
import Button from '../../misc/Button';
import InputSettingsField from './InputSettingsField';
import { PageLogicCondition } from './CollectionConditionalLogicSettingsForm';

import ThemeIcons from '../../../theme/Icons';
import ThemeContext from '../../../theme/ThemeContext';

import ConditionalLogic from '../../../constants/ConditionalLogic';
import FieldTableColumnTypes from '../../../constants/FieldTableColumnTypes';

/**
 * Column types that can be used as a cell-visibility condition source. Mirrors the set the
 * collection column-condition builder supports.
 */
export const CELL_LOGIC_COLUMN_TYPES = [
  FieldTableColumnTypes.text,
  FieldTableColumnTypes.number,
  FieldTableColumnTypes.dropdown,
  FieldTableColumnTypes.multiSelect,
  FieldTableColumnTypes.barcode,
];

const StyledWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledStepLabel = styled.div`
  font-weight: 500;
  margin-left: 6px;
  font-size: 14px;
  color: ${(props) => props.theme.fontColorPrimary};
`;

const StyledSectionLabel = styled.div`
  font-weight: 600;
  font-size: 12px;
  margin-bottom: 6px;
  color: ${(props) => props.theme.fontColorSecondary};
`;

const StyledConditionColumn = styled.div`
  display: flex;
  flex-direction: column;
  ${(props) => (props.conditions && props.conditions.length > 1
    ? 'max-height: 450px; overflow-y: auto;'
    : '')};
`;

const StyledConditionRow = styled.div`
  display: flex;
  flex: 1;
  margin-bottom: 8px;
  flex-direction: row;
  align-items: center;
`;

const StyledConditionWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  padding: 8px;
  margin-bottom: 8px;
  padding-bottom: 2px;
  border-radius: ${(props) => props.theme.borderRadius}px;
  border: 1px solid ${(props) => props.theme.borderColor};
  background-color: ${(props) => props.theme.secondaryColor};
`;

const StyledConditionSelectWrapper = styled.div`
  display: flex;
`;

const StyledConditionDelete = styled.div`
  display: flex;
  flex: 1;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
`;

/**
 * Cell-visibility authoring form. Unlike Conditional Logic (which can only reference document
 * fields) this supports two condition sources:
 *   - Column: another column in THIS table (resolved against the row's sibling cells)
 *   - Page:   a document field elsewhere in the form
 *
 * Column conditions are stored as { column, condition, value } (table) or
 * { schema, column, condition, value } (collection, so column renames stay in sync). Page
 * conditions reuse the shared PageLogicCondition ({ file, page, field, condition, value }).
 */
export const CellVisibilityLogicSettingsForm = ({
  fileId,
  targetLabel,
  targetColumnId,
  tableColumns,
  schemaId,
  fields,
  sortedPages,
  logic,
  onChange,
}) => {

  const theme = useContext(ThemeContext);

  const formattedSortedPages = (sortedPages || [])
    .map((page, i) => ({ ...page, label: `Page ${i + 1}` }));

  const fieldLookup = {};
  (fields || []).forEach((f) => fieldLookup[f._id] = f);

  const eligibleColumns = (tableColumns || []).filter((col) => (
    col._id !== targetColumnId
    && !col.deleted
    && CELL_LOGIC_COLUMN_TYPES.includes(col.type)
  ));

  const handleChange = (key, value) => {
    const nextLogic = JSON.parse(JSON.stringify(logic));
    nextLogic[key] = value;
    if (onChange) onChange(nextLogic);
  };

  const handleColumnConditionCreate = () => {
    const nextLogic = JSON.parse(JSON.stringify(logic));
    const newCondition = schemaId ? { schema: schemaId, column: null } : { column: null };
    if (nextLogic.conditions) {
      nextLogic.conditions.push(newCondition);
    } else {
      nextLogic.conditions = [newCondition];
    }
    onChange(nextLogic);
  };

  const handlePageConditionCreate = () => {
    const nextLogic = JSON.parse(JSON.stringify(logic));
    if (nextLogic.conditions) {
      nextLogic.conditions.push({ file: fileId });
    } else {
      nextLogic.conditions = [{ file: fileId }];
    }
    onChange(nextLogic);
  };

  const handleConditionChange = (index, changes) => {
    const nextLogic = JSON.parse(JSON.stringify(logic));
    if (!nextLogic.conditions[index]) return;
    nextLogic.conditions[index] = {
      ...nextLogic.conditions[index],
      ...changes,
    };
    onChange(nextLogic);
  };

  const handleConditionDelete = (index) => {
    const nextLogic = JSON.parse(JSON.stringify(logic));
    nextLogic.conditions.splice(index, 1);
    onChange(nextLogic);
  };

  const indexedConditions = (logic?.conditions || []).map((c, i) => ({ ...c, _originalIndex: i }));
  // Page conditions always carry `file`; everything else is a column condition.
  const pageConditions = indexedConditions.filter((c) => c.file !== undefined);
  const columnConditions = indexedConditions.filter((c) => c.file === undefined);

  return (
    <StyledWrapper>
      <StyledConditionRow>
        <div style={{ width: '75px' }}>
          <InputSettingsField
            type="dropdown"
            value={logic.action}
            options={[
              { _id: 'show', value: 'Show' },
              { _id: 'hide', value: 'Hide' },
            ]}
            onChange={(i, value) => handleChange('action', value)}
          />
        </div>
        <StyledStepLabel theme={theme}>
          this
          {' '}
          {targetLabel}
          {' '}
          when
        </StyledStepLabel>
      </StyledConditionRow>

      <StyledConditionRow>
        <div style={{ width: '75px' }}>
          <InputSettingsField
            type="dropdown"
            inputStyles={{ maxWidth: '100px' }}
            value={logic.eval}
            options={[
              { _id: 'and', value: 'All' },
              { _id: 'or', value: 'Any' },
            ]}
            onChange={(i, value) => handleChange('eval', value)}
          />
        </div>
        <StyledStepLabel theme={theme}>of the following conditions match</StyledStepLabel>
      </StyledConditionRow>

      <StyledSectionLabel theme={theme}>Column</StyledSectionLabel>
      <StyledConditionRow style={{ marginBottom: 0 }}>
        <StyledConditionColumn style={{ flex: 1 }} conditions={columnConditions}>
          {columnConditions.map((condition) => (
            <TableColumnLogicCondition
              key={condition._originalIndex}
              index={condition._originalIndex}
              columns={eligibleColumns}
              column={condition.column}
              condition={condition.condition}
              value={condition.value}
              onChange={handleConditionChange}
              onDelete={handleConditionDelete}
            />
          ))}
        </StyledConditionColumn>
      </StyledConditionRow>
      <StyledConditionRow>
        <Button icon={ThemeIcons.misc.add} onClick={handleColumnConditionCreate} />
      </StyledConditionRow>

      <StyledSectionLabel theme={theme}>Page</StyledSectionLabel>
      <StyledConditionRow style={{ marginBottom: 0 }}>
        <StyledConditionColumn style={{ flex: 1 }} conditions={pageConditions}>
          {pageConditions.map((condition) => (
            <PageLogicCondition
              key={condition._originalIndex}
              index={condition._originalIndex}
              file={condition.file}
              page={condition.page}
              field={condition.field}
              condition={condition.condition}
              value={condition.value}
              fieldLookup={fieldLookup}
              pages={formattedSortedPages}
              onChange={handleConditionChange}
              onDelete={handleConditionDelete}
            />
          ))}
        </StyledConditionColumn>
      </StyledConditionRow>
      <StyledConditionRow>
        <Button icon={ThemeIcons.misc.add} onClick={handlePageConditionCreate} />
      </StyledConditionRow>
    </StyledWrapper>
  );

};

/**
 * Single "Column" condition row. Lists this table's own eligible columns (target excluded) and
 * builds a { column, condition, value } comparison resolved against the row's sibling cells.
 */
export const TableColumnLogicCondition = ({
  index,
  columns,
  column,
  condition,
  value,
  onChange,
  onDelete,
}) => {

  const theme = useContext(ThemeContext);

  const columnOptions = (columns || []).map((col) => ({
    _id: col._id,
    value: col.title || col.identifier || col._id,
  }));

  const selectedColumn = (columns || []).find((col) => col._id === column);

  let conditionOptions = [];
  let valueNode = null;

  if (selectedColumn) {
    switch (selectedColumn.type) {
      case FieldTableColumnTypes.text:
      case FieldTableColumnTypes.barcode:
        conditionOptions = [
          { _id: ConditionalLogic.conditions.filled, value: 'is filled' },
          { _id: ConditionalLogic.conditions.empty, value: 'is empty' },
          { _id: ConditionalLogic.conditions.equal, value: 'is' },
          { _id: ConditionalLogic.conditions.notEqual, value: 'is not' },
          { _id: ConditionalLogic.conditions.contain, value: 'contains' },
        ];
        valueNode = (
          <InputSettingsField
            label="Value"
            value={value}
            type="text"
            onChange={(i, val) => onChange(index, { value: val })}
          />
        );
        break;

      case FieldTableColumnTypes.number:
        conditionOptions = [
          { _id: ConditionalLogic.conditions.filled, value: 'is filled' },
          { _id: ConditionalLogic.conditions.empty, value: 'is empty' },
          { _id: ConditionalLogic.conditions.equal, value: 'is' },
          { _id: ConditionalLogic.conditions.notEqual, value: 'is not' },
          { _id: ConditionalLogic.conditions.greaterThan, value: 'is greater than' },
          { _id: ConditionalLogic.conditions.lessThan, value: 'is less than' },
        ];
        valueNode = (
          <InputSettingsField
            label="Value"
            value={value}
            type="number"
            onChange={(i, val) => onChange(index, { value: val })}
          />
        );
        break;

      case FieldTableColumnTypes.dropdown:
      case FieldTableColumnTypes.multiSelect: {
        conditionOptions = [
          { _id: ConditionalLogic.conditions.filled, value: 'is filled' },
          { _id: ConditionalLogic.conditions.empty, value: 'is empty' },
          { _id: ConditionalLogic.conditions.equal, value: 'is' },
          { _id: ConditionalLogic.conditions.notEqual, value: 'is not' },
        ];

        const optionItems = (selectedColumn.options || [])
          .filter((opt) => !opt.deleted)
          .map((opt) => ({ _id: opt._id, value: opt.value }));

        valueNode = (
          <InputSettingsField
            label="Value"
            value={value}
            type="dropdown"
            options={optionItems}
            onChange={(i, val) => onChange(index, { value: val })}
          />
        );
        break;
      }

      default:
        break;
    }
  }

  const showValueField = selectedColumn
    && ![ConditionalLogic.conditions.filled, ConditionalLogic.conditions.empty].includes(condition);

  return (
    <StyledConditionWrapper theme={theme}>
      <StyledConditionColumn style={{ flex: 1 }}>
        <StyledConditionRow>
          <InputSettingsField
            label="Column"
            value={column}
            type="dropdown"
            options={columnOptions}
            onChange={(i, columnId) => onChange(index, { column: columnId })}
          />
        </StyledConditionRow>

        {selectedColumn && (
          <StyledConditionRow>
            <InputSettingsField
              label="Condition"
              value={condition}
              type="dropdown"
              options={conditionOptions}
              onChange={(i, val) => onChange(index, { condition: val })}
            />
          </StyledConditionRow>
        )}

        {selectedColumn && condition && showValueField && (
          <StyledConditionRow>
            <StyledConditionSelectWrapper>{valueNode}</StyledConditionSelectWrapper>
          </StyledConditionRow>
        )}
      </StyledConditionColumn>

      <StyledConditionColumn>
        <StyledConditionDelete>
          <Icon
            color={theme.dangerColor}
            icon={ThemeIcons.settings.delete}
            size="sm"
            onClick={() => onDelete(index)}
          />
        </StyledConditionDelete>
      </StyledConditionColumn>
    </StyledConditionWrapper>
  );

};

export default CellVisibilityLogicSettingsForm;
